import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import {
  Search,
  Plus,
  Pencil,
  MoreVertical,
  Trash2,
  Loader2,
  ClipboardList
} from 'lucide-react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import styles from './LeaveTypes.module.css';
import LeaveTypeDrawer from './LeaveTypeDrawer';
import Pagination from '../../../components/Common/Pagination/Pagination';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import Footer from '../../../components/Common/Footer/Footer';
import NoData from '../../../components/Common/NoData/NoData';

const MySwal = withReactContent(Swal);

const LeaveTypes = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [filteredLeaveTypes, setFilteredLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 10
  });

  const fetchLeaveTypes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/leave-type');
      const data = Array.isArray(response.data) ? response.data : [];
      setLeaveTypes(data);
      setFilteredLeaveTypes(data);
      setPagination(prev => ({
        ...prev,
        total: data.length,
        last_page: Math.ceil(data.length / prev.per_page)
      }));
    } catch (error) {
      console.error('Error fetching leave types:', error);
      toast.error('Failed to fetch leave types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  useEffect(() => {
    const filtered = leaveTypes.filter(lt => 
      lt.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lt.short_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredLeaveTypes(filtered);
    setPagination(prev => ({
      ...prev,
      current_page: 1,
      total: filtered.length,
      last_page: Math.ceil(filtered.length / prev.per_page)
    }));
  }, [searchTerm, leaveTypes]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    const handleScroll = () => {
      if (openMenuIndex !== null) setOpenMenuIndex(null);
    };
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openMenuIndex]);

  const toggleActionMenu = (e, id, leaveType) => {
    e.stopPropagation();
    if (openMenuIndex === id) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
      setOpenMenuIndex(id);
      setEditingLeaveType(leaveType);
    }
  };

  const handleAddClick = () => {
    setEditingLeaveType(null);
    setIsDrawerOpen(true);
  };

  const handleEditClick = (leaveType) => {
    setEditingLeaveType(leaveType);
    setIsDrawerOpen(true);
    setOpenMenuIndex(null);
  };

  const handleDelete = async (leaveType) => {
    setOpenMenuIndex(null);
    MySwal.fire({
      html: `
        <div class="custom-icon-wrapper-swal">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 12px 0;">Delete Leave Type?</h2>
        <p style="margin: 0 0 8px 0; color: #475569; font-size: 15px; line-height: 1.5;">
          Are you sure you want to delete <strong>${leaveType.name}</strong>?
        </p>
        <p style="margin: 0 0 0 0; color: #94a3b8; font-size: 13px;">
          This action will permanently remove this record from the database.
        </p>
      `,
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'No, Keep it',
      customClass: {
        popup: 'custom-swal-popup',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
        actions: 'custom-swal-actions-row'
      },
      buttonsStyling: false
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/leave-type/${leaveType.id}`);
          toast.success('Leave type deleted successfully');
          fetchLeaveTypes();
        } catch (error) {
          console.error('Error deleting leave type:', error);
          toast.error('Failed to delete leave type');
        }
      }
    });
  };

  const getStatusBadgeClass = (status) => {
    if (status === 1 || status === '1') return styles['badge-active'];
    if (status === 0 || status === '0') return styles['badge-inactive'];
    return '';
  };

  const paginatedData = filteredLeaveTypes.slice(
    (pagination.current_page - 1) * pagination.per_page,
    pagination.current_page * pagination.per_page
  );

  return (
    <div className={styles['leave-types-container']}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Leave Types</h1>
              <p>Define and manage different types of leave available for employees.</p>
            </div>
            <button className={styles['add-btn']} onClick={handleAddClick}>
              <Plus size={20} />
              <span>Add Leave Type</span>
            </button>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search leave types..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' }
                  ]}
                  onChange={(opt) => setPagination(prev => ({ ...prev, per_page: opt.value, current_page: 1 }))}
                />
              </div>
            </div>
          </div>

          <div className={[styles['custom-table-container'], 'card'].join(' ')}>
            {/* Desktop Table */}
            <div className={styles['custom-table-wrapper']}>
              <table className={styles['custom-table']}>
                <thead>
                  <tr>
                    <th>NAME</th>
                    <th>CODE</th>
                    <th>STATUS</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', padding: '100px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                          <Loader2 size={40} className={styles['spinner']} style={{ color: '#f97316' }} />
                          <span style={{ color: '#64748b', fontWeight: 600 }}>Fetching leave types...</span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((leaveType, index) => (
                      <tr key={leaveType.id}>
                        <td className={styles['name-cell']}>
                          {leaveType.name}
                        </td>
                        <td className={styles['code-cell']}>{leaveType.short_name}</td>
                        <td>
                          <span className={`${styles['status-badge']} ${getStatusBadgeClass(leaveType.status)}`}>
                            {leaveType.status === 1 || leaveType.status === '1' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className={styles['actions-cell']}>
                          <div className={`action-wrapper ${styles['action-wrapper']}`}>
                            <button
                              className={`${styles['action-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                              onClick={(e) => toggleActionMenu(e, index, leaveType)}
                            >
                              <MoreVertical size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <NoData message="No leave types found." colSpan={4} isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className={styles['leave-type-cards']}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 size={32} className={styles['spinner']} style={{ color: '#f97316' }} />
                </div>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((leaveType, index) => (
                  <div key={leaveType.id} className={styles['leave-type-card']}>
                    <div className={styles['card-main']}>
                      <div className={styles['card-info']}>
                        <span className={styles['card-name']}>{leaveType.name}</span>
                        <span className={styles['card-short-name']}>{leaveType.short_name}</span>
                      </div>
                      <div className={`action-wrapper ${styles['action-wrapper']}`}>
                        <button
                          className={styles['action-btn']}
                          onClick={(e) => toggleActionMenu(e, index, leaveType)}
                        >
                          <MoreVertical size={18} />
                        </button>
                      </div>
                    </div>
                    <div className={styles['card-footer']}>
                      <span className={`${styles['status-badge']} ${getStatusBadgeClass(leaveType.status)}`}>
                        {leaveType.status === 1 || leaveType.status === '1' ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <NoData message="No leave types found." isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
              />
            </div>
          </div>
          
          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={pagination.current_page}
              totalItems={pagination.total}
              itemsPerPage={pagination.per_page}
              onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
            />
          </div>

          {/* Global Fixed Action Menu */}
          {openMenuIndex !== null && (
            <div
              className={styles['action-dropdown']}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                zIndex: 9999
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => handleEditClick(editingLeaveType)}>
                <Pencil size={16} /> Edit
              </button>
              <div className={styles['dropdown-divider']}></div>
              <button className={styles['deselect']} onClick={() => handleDelete(editingLeaveType)}>
                <Trash2 size={16} /> Delete
              </button>
            </div>
          )}
        
          <Footer />
        </main>
      </div>

      <LeaveTypeDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={() => {
          setIsDrawerOpen(false);
          fetchLeaveTypes();
        }}
        leaveType={editingLeaveType}
      />
    </div>
  );
};

export default LeaveTypes;
